import { useEffect, useState } from "react";

import api from "../../../lib/api";
import type { HighCourtRow } from "./highcourt/types";

export interface CourtDashboardStats {
  highCourtCount: number;
  benchCount: number;
  courtHallCount: number;
  districtCount: number;
  courtRoomCount: number;
}

const emptyStats: CourtDashboardStats = {
  highCourtCount: 0,
  benchCount: 0,
  courtHallCount: 0,
  districtCount: 0,
  courtRoomCount: 0,
};

export const useCourtDashboardStats = (highCourts: HighCourtRow[]) => {
  const [stats, setStats] = useState<CourtDashboardStats>(emptyStats);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      setIsLoading(true);

      try {
        const [highCourtBreakdowns, districts] = await Promise.all([
          Promise.all(
            highCourts.map(async (court) => {
              const benches = await fetchRows(
                `/api/court/get-benches/${court._id}?page=1&limit=1000`,
              );

              const courtHalls = await Promise.all(
                benches.map((bench: any) =>
                  fetchRows(
                    `/api/court/get-bench-rooms/${bench._id}?page=1&limit=1000`,
                  ),
                ),
              );

              return {
                benches: benches.length,
                courtHalls: courtHalls.reduce(
                  (total, rooms) => total + rooms.length,
                  0,
                ),
              };
            }),
          ),
          fetchRows("/api/court/get-districts?page=1&limit=1000"),
        ]);

        const districtCourtRooms = await Promise.all(
          districts.map(async (district: any) => {
            const complexes = await fetchRows(
              `/api/court/get-complexes/${district._id}`,
            );

            const complexRoomCounts = await Promise.all(
              complexes.map(async (complex: any) => {
                if (!complex.courtEstablishment) {
                  const courtRooms = await fetchRows(
                    `/api/court/get-courtrooms/${complex._id}`,
                  );
                  return courtRooms.length;
                }

                const establishments = await fetchRows(
                  `/api/court/get-establishments/${complex._id}`,
                );
                const establishmentRooms = await Promise.all(
                  establishments.map((establishment: any) =>
                    fetchRows(`/api/court/get-courtrooms/${establishment._id}`),
                  ),
                );

                return establishmentRooms.reduce(
                  (total, rooms) => total + rooms.length,
                  0,
                );
              }),
            );

            return complexRoomCounts.reduce((total, count) => total + count, 0);
          }),
        );

        if (cancelled) return;

        setStats({
          highCourtCount: highCourts.length,
          benchCount: highCourtBreakdowns.reduce(
            (total, item) => total + item.benches,
            0,
          ),
          courtHallCount: highCourtBreakdowns.reduce(
            (total, item) => total + item.courtHalls,
            0,
          ),
          districtCount: districts.length,
          courtRoomCount: districtCourtRooms.reduce(
            (total, count) => total + count,
            0,
          ),
        });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadStats();

    return () => {
      cancelled = true;
    };
  }, [highCourts]);

  return { stats, isLoading };
};

const fetchRows = async (url: string) => {
  try {
    const response = await api.get(url);
    const data = response?.data;

    if (data?.status === false) return [];
    return Array.isArray(data?.data) ? data.data : [];
  } catch {
    return [];
  }
};
